import { CHAIN_ID, Context, extractEvents, TransactionEvent } from '@derivation-tech/context';
import { getRevertReason } from '@derivation-tech/tx-plugin';
import { ErrorDescription } from '@ethersproject/abi/lib.esm/interface';
import { TransactionReceipt, TransactionResponse } from '@ethersproject/abstract-provider';
import { JsonRpcProvider, JsonRpcSigner, Provider } from '@ethersproject/providers';
import { BigNumber, PopulatedTransaction } from 'ethers';
import { BytesLike, Interface } from 'ethers/lib/utils';

import { OPERATION_TX_TYPE, ZERO } from '@/constants';
import { DAPP_CHAIN_CONFIGS } from '@/constants/chain';
import { EVENT_NAMES, OPERATE_EVENT_MAPPING } from '@/constants/event';
import { TX_INTERFACE_MAPPING } from '@/constants/tx';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useAgentStore } from '@/features/agent/store';
import { INotificationArgs } from '@/hooks/useTxNotification';
import i18n from '@/i18n';
import { IEventSource, IParsedEventLog, IParsedTemplate } from '@/types/transaction';
import { Side } from '@synfutures/sdks-perp';
import _ from 'lodash';
import { getSynContract } from './contract';
import { sendGelatoTransaction } from './gelatoTransaction';
import { parseTxEventLog, txSendNotification } from './notification';
import { currentUTCTime } from './timeUtils';
import { fixBalanceNumberDecimalsTo18 } from './token';

export function getTxMappingInterface(txType: OPERATION_TX_TYPE):
  | {
      interfaces: Interface[];
      events: string[];
    }
  | undefined {
  return OPERATE_EVENT_MAPPING[txType];
}

export async function getEventLogs(
  txType: OPERATION_TX_TYPE,
  receipt: TransactionReceipt,
): Promise<TransactionEvent[] | undefined> {
  try {
    const interfaceMapping = getTxMappingInterface(txType);
    if (interfaceMapping) {
      const eventLogs = await extractEvents(
        // chainId,
        receipt,
        interfaceMapping.interfaces,
        interfaceMapping.events,
      );
      console.log('🚀 ~ file: hook.ts ~ line 64 ~ res', eventLogs);
      return eventLogs;
    }
  } catch (error) {
    console.log('🚀 ~ file: hook.ts ~ line 66 ~ error', error);
  }
}

/**
 * is filter event
 * @param param0
 * @returns
 */
export function isFilterEvent({ eventSource }: { eventSource: IEventSource }): boolean {
  let isShow = true;
  if (eventSource.eventName === 'ClaimFee' && eventSource.args?.feeAmount) {
    const fee = BigNumber.from(eventSource.args.feeAmount);
    isShow = !fee.eq(ZERO);
  }

  return isShow;
}

export function replaceRevert(reason: string): string {
  if (reason?.startsWith('execution reverted:')) {
    return reason.replace('execution reverted: ', '');
  }
  return reason;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export function getRevertReasonByError(error: any): string {
  if (error.reason) {
    const reason: string = error.reason;
    return replaceRevert(reason);
  }
  return '';
}

/**
 * get new revert reason by interface parse error
 * @param txType
 * @param data
 * @returns
 */
export function getNewRevertReasonsByTxType(
  txType: OPERATION_TX_TYPE,
  data: BytesLike,
): ErrorDescription[] | undefined {
  if (TX_INTERFACE_MAPPING[txType]) {
    try {
      const res: ErrorDescription[] = [];
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      for (let i = 0; i < TX_INTERFACE_MAPPING[txType]!.length; i++) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const abiInterface = TX_INTERFACE_MAPPING[txType]![i];
        const errorDesc = abiInterface.parseError(data);
        if (errorDesc) {
          res.push(errorDesc);
        }
      }
      return res;
    } catch (error) {
      console.log('🚀 ~ file: tx.tsx ~ line 274 ~ getRevertReasonsByTxType ~ error', error);
    }
  }
  return undefined;
}

/**
 * get new revert reason by sdk
 * @param param0
 * @returns
 */
export async function getSDKRevertReasons({
  txType,
  provider,
  txResponse,
}: {
  txType: OPERATION_TX_TYPE;
  txResponse: TransactionResponse | string;
  provider: Provider;
}): Promise<string | ErrorDescription | undefined> {
  if (TX_INTERFACE_MAPPING[txType]) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const errorDesc = await getRevertReason(provider, txResponse, TX_INTERFACE_MAPPING[txType]!);
      return errorDesc;
    } catch (error) {}
  }
  return undefined;
}

export async function parseReceiptEventMapping({
  txType,
  receipt,
  instrument,
  extra,
}: {
  chainId: CHAIN_ID;
  userAddr: string;
  txType: OPERATION_TX_TYPE;
  receipt: TransactionReceipt;
  extra?: { [key: string]: unknown };
  instrument: {
    quoteSymbol: string;
    baseSymbol: string;
    isInverse: boolean;
    quoteDecimal?: number;
  };
}): Promise<IParsedEventLog[]> {
  let res: IParsedEventLog[] = [];
  const eventLogs = await getEventLogs(txType, receipt);
  if (eventLogs) {
    eventLogs.forEach((eventLog) => {
      const eventSource: IEventSource = {
        txType,
        eventName: eventLog.name,
        args: eventLog.args,
        blockNumber: eventLog.blockNumber,
        logIndex: eventLog.logIndex,
        timestamp: Math.round(currentUTCTime() / 1000),
        txHash: eventLog.txHash,
        // receipt: eventLog.receipt,
      };
      const parsedLog = parseTxEventLog({
        eventSource: eventSource,
        instrument,
      }) as IParsedEventLog;
      if (parsedLog) {
        const isFilter = isFilterEvent({ eventSource });
        isFilter && res.push(parsedLog);
      }
    });
  }
  if (res.length > 1) {
    if (txType === OPERATION_TX_TYPE.SETTLE) {
      // if settle with cancel, add cancel balance to settle balance
      const settleLog = res.find((log) => log.eventSource.eventName === EVENT_NAMES.Settle);
      if (settleLog) {
        let settleBalance = WrappedBigNumber.from(0);
        if (res.find((log) => log.eventSource.eventName === EVENT_NAMES.Gather)) {
          try {
            res
              .filter((log) => log.eventSource.eventName === EVENT_NAMES.Gather)
              .forEach((evLog) => {
                if (evLog.eventSource?.args) {
                  const balance = WrappedBigNumber.from(
                    fixBalanceNumberDecimalsTo18(
                      BigNumber.from(evLog?.eventSource?.args?.quantity),
                      instrument?.quoteDecimal || 18,
                    ),
                  );
                  if (balance.gt(0)) {
                    settleBalance = settleBalance.add(balance);
                  }
                }
              });
          } catch (error) {}
        }
        if (settleBalance.gt(0)) {
          settleLog.templateArgs.balance = settleBalance.formatDisplayNumber();
        }
        res = [settleLog];
      }
    }
    // multi cancel order
    else if (txType === OPERATION_TX_TYPE.CANCEL_ORDER) {
      const result: IParsedEventLog = { eventSource: res[0].eventSource, descTemplate: '', templateArgs: {} };
      result.descTemplate = `notification.cancelOrder.multiSuccessful`;
      result.templateArgs = {
        orderNumber: res.length.toString(),
      };
      res = [result];
    }
    // batch place scaled order
  }
  if (txType === OPERATION_TX_TYPE.BATCH_PLACE_SCALED_ORDER) {
    try {
      const baseSize: BigNumber = extra?.baseSize as BigNumber;
      const side = extra?.side as Side;
      const baseSymbol = instrument.baseSymbol;
      const orderCount = res.length;
      let upperPrice = WrappedBigNumber.from((extra?.upperPrice as string) || 0);
      let lowerPrice = WrappedBigNumber.from((extra?.lowerPrice as string) || 0);
      if (upperPrice.lt(lowerPrice)) {
        [upperPrice, lowerPrice] = [lowerPrice, upperPrice];
      }
      const size = WrappedBigNumber.from(baseSize);
      const tradeType = side === Side.LONG ? 'common.buy' : 'common.sell';

      const result: IParsedEventLog = {
        eventSource: _.get(res, [0, 'eventSource']),
        descTemplate: '',
        templateArgs: {},
      };
      result.descTemplate = `notification.${OPERATION_TX_TYPE.BATCH_PLACE_SCALED_ORDER}.successDesc`;
      result.templateArgs = {
        size: size.abs().formatDisplayNumber(),
        base: baseSymbol || '',
        tradeType: tradeType,
        lowerPrice: lowerPrice.formatPriceString(),
        upperPrice: upperPrice.formatPriceString(),
        orderCount: orderCount.toString(),
      };
      res = [result];
    } catch (error) {
      console.log('🚀 ~ useEventMapping ~ error:', error);
    }
  }

  return res;
}

type HandleSendAgentOrNormalTxParams = {
  signer: JsonRpcSigner;
  userAddr: string;
  chainId: number;
  provider: JsonRpcProvider | undefined;
  sendingTemplate?: IParsedTemplate;
  sendTxNotification?: (args: INotificationArgs) => void;
  txType: OPERATION_TX_TYPE;
  sdk: Context;
  handlePopulatedTx: () => Promise<PopulatedTransaction>;
  handleGelatoTxTargetData: (agentAddr: string) => Promise<string>;
  needEnableAgent?: boolean;
  instrumentAddr?: string;
  needBatchData?: boolean;
};

/**
 * Handles sending a transaction through either an agent or normal transaction flow
 * @param signer - JsonRpcSigner instance for signing transactions
 * @param userAddr - User's wallet address
 * @param chainId - Current blockchain network ID
 * @param instrumentAddr - Address of the trading instrument contract
 * @param provider - Optional JsonRpcProvider instance
 * @param sendingTemplate - Optional template for transaction notifications
 * @param sendTxNotification - Optional callback for sending transaction notifications
 * @param txType - Type of operation transaction
 * @param sdk - Context instance from SDK
 * @param handlePopulatedTx - Callback that returns populated transaction data
 * @param handleGelatoTxTargetData - Callback that returns encoded transaction data for Gelato
 * @returns Transaction response or Gelato task result
 */
export async function handleSendAgentOrNormalTx({
  signer,
  userAddr,
  chainId,
  instrumentAddr,
  provider,
  sendingTemplate,
  sendTxNotification,
  txType,
  handlePopulatedTx,
  handleGelatoTxTargetData,
  needEnableAgent = true && !DAPP_CHAIN_CONFIGS[chainId]?.disable1CT,
  needBatchData = true,
}: HandleSendAgentOrNormalTxParams) {
  if (!provider) {
    provider = signer?.provider;
  }
  const agentSettings = useAgentStore.getState().userAgentSettings[userAddr];
  const agentAddr = agentSettings?.agentAddress;
  const isEnableAgentMode = agentSettings?.isEnableAgentMode;
  if (agentAddr && isEnableAgentMode && needEnableAgent) {
    const routerContract = getSynContract({
      chainId,
      type: 'router',
      signerOrProvider: signer,
    });

    if (!routerContract) {
      throw new Error('Router or Gate contract not found');
    }

    const targetData = await handleGelatoTxTargetData(agentAddr);

    let batchData = targetData;
    if (needBatchData) {
      batchData = routerContract.interface.encodeFunctionData('batch', [
        agentAddr, // signer address
        userAddr,
        instrumentAddr, // targetContract address
        targetData,
        false, // userPaid
      ]);
    }

    console.log('🚀 ~ batchData:', batchData);

    const result = await sendGelatoTransaction({
      chainId,
      batchData: batchData,
      // batchData: populatedTx?.data || '',
      targetContract: routerContract.address,
      userAddr, // Add userAddr parameter
      provider,
      hooks: {
        onStart: (taskId) => {
          txSendNotification({
            taskId,
            txType,
            sendingTemplate,
            sendTxNotification,
          });
        },
        onError: (error, taskId) => {
          sendTxNotification?.({
            message: `${i18n.t(`notification.${txType}.error`)}`,
            description: error || `${i18n.t(`notification.${txType}.errorDesc`)}`,
            taskId,
            type: 'error',
          });
        },
      },
    });
    if (result.success && result.taskId && result?.response) {
      return { taskId: result.taskId, txResult: result.response };
    }
  } else {
    const populatedTx = await handlePopulatedTx();
    return signer.sendTransaction(populatedTx);
  }
}
